import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SettingsSavedNotice from "./components/SettingsSavedNotice";
import { useAuth } from "./authContext";

const MIN_PASSWORD_LENGTH = 8;

export default function ResetPasswordView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { resetPassword } = useAuth();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const token = String(searchParams.get("token") || "").trim();

  useEffect(() => {
    if (!notice) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setNotice(""), 3600);
    return () => window.clearTimeout(timeoutId);
  }, [notice]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    if (password !== confirmPassword) {
      setError("The passwords do not match.");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = await resetPassword(token, password);
      setPassword("");
      setConfirmPassword("");

      if (payload?.authenticated) {
        navigate("/", { replace: true });
      } else if (payload?.requiresEmailVerification) {
        setNotice(payload?.verification?.url
          ? `Password updated. Verify your email next: ${payload.verification.url}`
          : "Password updated. Verify your email before logging in.");
      } else {
        setNotice("Password updated.");
      }
    } catch (submitError) {
      setError(submitError.message || "Could not reset password.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="account-shell">
        <section className="account-settings-panel" style={{ maxWidth: "760px", margin: "0 auto" }}>
        <div className="account-page-header">
          <div>
            <h1 className="account-page-title">Reset Password</h1>
            <p className="account-copy">Set a new password for your account.</p>
          </div>
        </div>

        {!token ? <div className="account-error" style={{ marginBottom: "16px" }}>A reset token is required.</div> : null}

        <form onSubmit={handleSubmit} style={{ display: "grid", gap: "16px" }}>
          <label style={{ display: "grid", gap: "8px", color: "#334155", fontWeight: 700 }}>
            New password
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              style={{ height: "46px", borderRadius: "14px", border: "1px solid #cbd5e1", padding: "0 14px", fontSize: "14px" }}
            />
          </label>
          <div style={{ marginTop: "-8px", color: "#64748b", fontSize: "13px" }}>
            Minimum 8 characters.
          </div>

          <label style={{ display: "grid", gap: "8px", color: "#334155", fontWeight: 700 }}>
            Repeat new password
            <input
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              autoComplete="new-password"
              style={{ height: "46px", borderRadius: "14px", border: "1px solid #cbd5e1", padding: "0 14px", fontSize: "14px" }}
            />
          </label>

          {error ? <div className="account-error" style={{ margin: 0 }}>{error}</div> : null}
          <div className="account-save-row" style={{ justifyContent: "space-between", alignItems: "center" }}>
            <Link to="/auth" style={{ color: "#0f766e", fontWeight: 700, textDecoration: "none" }}>Back to login</Link>
            <button type="submit" className="account-action-button" disabled={isSubmitting || !token || password.length < MIN_PASSWORD_LENGTH || !confirmPassword}>
              {isSubmitting ? "Working..." : "Reset password"}
            </button>
          </div>
        </form>
      </section>
      </div>

      {notice ? <SettingsSavedNotice message={notice} /> : null}
    </>
  );
}